import React, { useState, useRef } from "react";

export default function RecipeCreator() {
  const [title, setTitle] = useState("");
  const [servings, setServings] = useState("");
  const [time, setTime] = useState("");
  const [hideServings, setHideServings] = useState(false);
  const [hideTime, setHideTime] = useState(false);
  const [ingredients, setIngredients] = useState([""]);
  const [instructions, setInstructions] = useState([""]);
  const [notes, setNotes] = useState("");

  const [lastAddedIngredientIndex, setLastAddedIngredientIndex] = useState(null);
  const [lastAddedInstructionIndex, setLastAddedInstructionIndex] = useState(null);


  const previewRef = useRef();

  const updateListItem = (list, setList, index, value) => {
    const updated = [...list];
    updated[index] = value;
    setList(updated);
  };

  const addListItem = (list, setList, setLastAdded) => {
    const newList = [...list, ""];
    setList(newList);
    setLastAdded(newList.length - 1);
  };

  const deleteListItem = (list, setList, index) => {
    const updated = list.filter((_, i) => i !== index);
    setList(updated);
  };

  const moveListItem = (list, setList, index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= list.length) return;
    const updated = [...list];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setList(updated);
  };

  return (
    <div className="grid grid-cols-2 gap-4 p-4">
      {/* Form */}
      <div className="space-y-4">
        <div>
          <label className="block font-semibold">Title</label>
          <input
            className="border p-2 w-full"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          <div className="flex-1">
            <label className="block font-semibold">Servings</label>
            <input
              className="border p-2 w-full"
              value={servings}
              onChange={(e) => setServings(e.target.value)}
              disabled={hideServings}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex-1">
            <label className="block font-semibold">Time</label>
            <input
              className="border p-2 w-full"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              disabled={hideTime}
            />
          </div>
        </div>

        <div>
          <label className="block font-semibold">Ingredients</label>
          {ingredients.map((ing, i) => (
            <div key={i} className="flex items-center gap-2 mt-1">
              <input
                className="border p-2 w-full"
                value={ing}
                onChange={(e) =>
                  updateListItem(ingredients, setIngredients, i, e.target.value)
                }
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addListItem(ingredients, setIngredients, setLastAddedIngredientIndex);
                    }
                  }}
                autoFocus={i === lastAddedIngredientIndex}
              />
              <button
                className="px-2 py-1 bg-red-500 text-white rounded"
                onClick={() => deleteListItem(ingredients, setIngredients, i)}
              >
                ✕
              </button>
              <button
                className="px-2 py-1 bg-gray-300 rounded"
                onClick={() => moveListItem(ingredients, setIngredients, i, -1)}
              >
                ↑
              </button>
              <button
                className="px-2 py-1 bg-gray-300 rounded"
                onClick={() => moveListItem(ingredients, setIngredients, i, 1)}
              >
                ↓
              </button>
            </div>
          ))}
          <button
            className="mt-2 px-3 py-1 bg-blue-500 text-white rounded"
            onClick={() => addListItem(ingredients, setIngredients)}
          >
            Add Ingredient
          </button>
        </div>

        <div>
          <label className="block font-semibold">Instructions</label>
          {instructions.map((inst, i) => (
            <div key={i} className="flex items-center gap-2 mt-1">
              <input
                className="border p-2 w-full"
                value={inst}
                onChange={(e) =>
                  updateListItem(instructions, setInstructions, i, e.target.value)
                }
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addListItem(instructions, setInstructions, setLastAddedInstructionIndex);
                    }
                  }}
                autoFocus={i === lastAddedInstructionIndex}  
              />
              <button
                className="px-2 py-1 bg-red-500 text-white rounded"
                onClick={() => deleteListItem(instructions, setInstructions, i)}
              >
                ✕
              </button>
              <button
                className="px-2 py-1 bg-gray-300 rounded"
                onClick={() => moveListItem(instructions, setInstructions, i, -1)}
              >
                ↑
              </button>
              <button
                className="px-2 py-1 bg-gray-300 rounded"
                onClick={() => moveListItem(instructions, setInstructions, i, 1)}
              >
                ↓
              </button>
            </div>
          ))}
          <button
            className="mt-2 px-3 py-1 bg-blue-500 text-white rounded"
            onClick={() => addListItem(instructions, setInstructions)}
          >
            Add Instruction
          </button>
        </div>

        <div>
          <label className="block font-semibold">Notes</label>
          <textarea
            className="border p-2 w-full"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      {/* Preview */}
        <div ref={previewRef} className="border rounded p-4 bg-gray-50 space-y-2">
          <h2 className="text-2xl font-bold">{title || "Recipe Title"}</h2>
          {!hideServings && servings && (
            <div>
              <strong>Servings:</strong> {servings}
            </div>
          )}
          {!hideTime && time && (
            <div>
              <strong>Time:</strong> {time}
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 mt-4 grid-print">
            {ingredients.filter(Boolean).length > 0 && (
              <div className="col-span-1">
                <h3 className="font-semibold mb-1">Ingredients</h3>
                <ul className="list-disc list-inside">
                  {ingredients.filter(Boolean).map((ing, i) => (
                    <li key={i}>{ing}</li>
                  ))}
                </ul>
              </div>
            )}

            {instructions.filter(Boolean).length > 0 && (
              <div className="col-span-2">
                <h3 className="font-semibold mb-1">Instructions</h3>
                <ol className="list-decimal list-inside">
                  {instructions.filter(Boolean).map((inst, i) => (
                    <li key={i}>{inst}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          {notes && (
            <div>
              <h3 className="font-semibold mt-2">Notes</h3>
              <p>{notes}</p>
            </div>
          )}
             
        </div>
        <div>
                <h1>Print</h1>
                <button
                      className="mt-2 px-3 py-1 bg-green-500 text-white rounded"
                      onClick={() => {
                        const printContent = previewRef.current;
                        const WinPrint = window.open('', '', 'width=900,height=650');
                        WinPrint.document.write(`
                          <html>
                            <head>
                              <title>${title || 'Recipe'}</title>
                              <style>
                                  body {
                                    font-family: sans-serif;
                                    padding: 20px;
                                  }
                                  h2 {
                                    font-size: 24px;
                                  }
                                  ul, ol {
                                    margin-left: 20px;
                                  }
                                  .grid-print {
                                    display: grid;
                                    grid-template-columns: 1fr 2fr;
                                    gap: 1rem;
                                  }
                            </style>
                            </head>
                            <body>${printContent.innerHTML}</body>
                          </html>
                        `);
                        WinPrint.document.close();
                        WinPrint.focus();
                        WinPrint.print();
                        WinPrint.close();
                      }}
                    >
                      Print Recipe
                    </button>
            </div>
       
    </div>
  );
}
